/** The longest side a photo is sent at. */
export const MAX_IMAGE_SIDE = 2048;
const JPEG_QUALITY = 0.88;

export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Draws the photo into a canvas at most {@link MAX_IMAGE_SIDE} px on its longest side (turned upright
 * first) and encodes it again. A canvas carries pixels only, so the re-encoded file has no Exif: no
 * GPS location, no camera serial. PNG and WebP keep their type; every other photo (HEIC included)
 * becomes JPEG. A GIF has no Exif and is sent as it is, so an animation keeps moving.
 */
export const prepareImage = async (file: File): Promise<PreparedImage> => {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    if (file.type === "image/gif") {
      return { blob: file, width: bitmap.width, height: bitmap.height };
    }

    const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas is not available");
    context.drawImage(bitmap, 0, 0, width, height);

    const type = file.type === "image/png" || file.type === "image/webp" ? file.type : "image/jpeg";
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((result) => (result ? resolve(result) : reject(new Error("Encoding failed"))), type, JPEG_QUALITY),
    );
    return { blob, width, height };
  } finally {
    bitmap.close();
  }
};

/** The display name after re-encoding: a HEIC photo sent as JPEG ends in ".jpg". */
export const preparedFileName = (name: string, type: string): string => {
  const extension = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp", "image/gif": "gif" }[type];
  if (!extension) return name;
  const base = name.includes(".") ? name.slice(0, name.lastIndexOf(".")) : name;
  const current = name.split(".").pop()?.toLowerCase();
  return current === extension || (extension === "jpg" && current === "jpeg") ? name : `${base}.${extension}`;
};
